import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronDown, Search } from "lucide-react";

/**
 * Searchable combobox. Props:
 * - items: [{ value, label, hint? }]
 * - value: selected value
 * - onChange: (value) => void
 * - placeholder: string
 * - emptyLabel: string shown when no matches
 * - testId: prefix for data-testid
 */
export default function SearchableSelect({
  items, value, onChange, placeholder = "Sélectionner…", emptyLabel = "Aucun résultat", testId,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => items.find((i) => i.value === value) || null, [items, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId ? `${testId}-trigger` : undefined}
          className="w-full flex items-center justify-between gap-2 border border-zinc-300 bg-white px-3 py-2 text-xs focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none hover:bg-zinc-50 transition-colors"
        >
          <span className={`truncate text-left ${selected ? "text-zinc-900" : "text-zinc-400"}`}>
            {selected ? (
              <>
                <span className="font-medium">{selected.label}</span>
                {selected.hint && <span className="text-zinc-400 font-mono ml-2">· {selected.hint}</span>}
              </>
            ) : placeholder}
          </span>
          <ChevronDown size={12} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start" sideOffset={4}>
        <Command>
          <div className="flex items-center border-b border-zinc-200 px-3" cmdk-input-wrapper="">
            <Search size={14} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
            <CommandInput
              placeholder="Rechercher…"
              className="flex h-10 w-full border-0 bg-transparent py-2 px-2 text-sm outline-none placeholder:text-zinc-400"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <CommandList className="max-h-[240px] overflow-auto app-scroll">
            <CommandEmpty className="py-6 text-center text-xs text-zinc-500">{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem
                  key={i.value}
                  value={`${i.label} ${i.hint || ""}`}
                  onSelect={() => {
                    onChange(i.value);
                    setOpen(false);
                  }}
                  data-testid={testId ? `${testId}-option-${i.value}` : undefined}
                  className="cursor-pointer aria-selected:bg-zinc-100 flex items-center gap-2 py-2 px-3 text-sm"
                >
                  <Check
                    size={14}
                    strokeWidth={1.5}
                    className={`shrink-0 ${value === i.value ? "text-[#002FA7]" : "text-transparent"}`}
                  />
                  <span className="flex-1 truncate">
                    <span className="font-medium">{i.label}</span>
                    {i.hint && <span className="text-zinc-400 font-mono ml-2">· {i.hint}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
